import { Schedule } from './schedule';
import { z } from 'zod';
import { TaskType } from './task';
export interface ScheduleSuperApi {
  resources: SuperApiResource[];
}

export interface SuperApiResource {
  events: SchedulerEvent[];
  resource: {
    id: string;
    name: string;
    externalId: string;
    role: string;
    team: string;
  };
  schedules: Schedule[];
}

export interface SchedulerEvent {
  claimNumber: string;
  endDateTime: string;
  eventName: string;
  reasonCode: string;
  externalId: string;
  insuredName: string;
  isTask: boolean;
  isRescheduleAllowed: boolean;
  peril: string;
  resourceId: string;
  source: string;
  startDateTime: string;
  taskReferenceId: string;
  tenantId: string;
  timeZoneId: string;
  data: SchedulerEvent;
  driveStartTime: string;
  lossLat: number;
  lossLong: number;
}

// new

/**
 * One day of Claim Owner capacity. `cap` is the limit available for that day —
 * the configured weekday value, reduced to zero when the resource cannot work it
 * — and `used` the count already consumed, counted independently, so
 * `cap 0 / used 3` is a real shape. Both are integers. `date` is a plain
 * calendar day on the requested window's axis, resolved in the board's selected
 * `scheduleTimeZoneId` — NOT in the resource's field-location zone.
 */
export const SuperApiClaimOwnerDayCapacity = z.object({
  date: z.string(),
  cap: z.number(),
  used: z.number(),
});

/**
 * One Monday–Sunday week of Claim Owner capacity. `used` is the FULL week's
 * figure — it includes elapsed days and days outside the requested range — so
 * it must not be recomputed by summing `days[].used`. `weeks` covers every
 * Mon–Sun week the requested range touches (normally two).
 */
export const SuperApiClaimOwnerWeekCapacity = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  cap: z.number(),
  used: z.number(),
});

/**
 * Claim Owner capacity for a scheduler resource. Mirrors the C#
 * `ClaimOwnerCapacityDetail` / `ClaimOwnerDayCapacity` / `ClaimOwnerWeekCapacity`
 * DTOs on `ScheduleResource`, so the field names here are fixed by the API.
 *
 * `timeZoneId` is the resource's field-location time zone, reported for
 * information only: no figure in this block is calculated in it. Both the day
 * dates and the Monday-to-Sunday week boundaries resolve in the board's selected
 * `scheduleTimeZoneId`. Its nullable annotation is NOT merely wire tolerance —
 * since 2026-08-26 an unresolvable zone no longer withholds the block, it is
 * reported with this field null, so null is an expected state rather than a
 * producer defect. Nothing renders it today.
 *
 * `days` and `weeks` are always serialized and never null, so they are required
 * here. An EMPTY list means the requested window produced no days — it does not
 * mean "not configured", which only a nullish block signals.
 *
 * Named `SuperApi…` (matching `SuperApiResource`/`SuperApiCalendar`/`SuperApiTask`)
 * because `ClaimOwnerCapacity` is already taken by the resource-admin shape in
 * `./resource`, which is a different, unrelated payload.
 */
export const SuperApiClaimOwnerCapacity = z.object({
  timeZoneId: z.string().nullish(),
  weeklyCap: z.number(),
  days: z.array(SuperApiClaimOwnerDayCapacity),
  weeks: z.array(SuperApiClaimOwnerWeekCapacity),
});

export type SuperApiClaimOwnerDayCapacity = z.infer<
  typeof SuperApiClaimOwnerDayCapacity
>;
export type SuperApiClaimOwnerWeekCapacity = z.infer<
  typeof SuperApiClaimOwnerWeekCapacity
>;
export type SuperApiClaimOwnerCapacity = z.infer<
  typeof SuperApiClaimOwnerCapacity
>;

export const SuperApiResource = z.object({
  id: z.string(),
  calendar: z.string(),
  externalId: z.string(),
  name: z.string(),
  role: z.string(),
  team: z.string(),
  // Populated only when the request sets `includeClaimOwnerCapacity`. Null or
  // absent is the SOLE "nothing to report" signal (AC 8) — the API never sends
  // a zero-filled object for it, so consumers must render the informational
  // message rather than 0 of 0. It has three causes and the payload does not say
  // which: no Claim Owner capacity record, a degraded capacity read (omitted for
  // every resource on the page), or the feature disabled for the tenant. Only
  // the first is about the resource, so the message must NOT state that nothing
  // is configured. A block that IS present is configured capacity even when
  // `days`/`weeks` are empty; empty means the window produced no days.
  claimOwnerCapacity: SuperApiClaimOwnerCapacity.nullish(),
});

export const CalendarInterval = z.object({
  name: z.string(),
  iconCls: z.string(),
  source: z.string(),
  startDate: z.union([z.string(), z.date()]),
  endDate: z.union([z.string(), z.date()]),
  recurrentStartDate: z.union([z.date(), z.string()]),
  recurrentEndDate: z.union([z.date(), z.string()]),
  isWorking: z.boolean(),
});

export const SuperApiCalendar = z.object({
  id: z.string(),
  name: z.string(),
  unspecifiedTimeIsWorking: z.boolean(),
  intervals: z.array(CalendarInterval),
});

export const EVENT_TYPE = [
  'no-location',
  'event',
  'task',
  ...Object.values(TaskType),
] as const;

export const SuperApiTask = z.object({
  insuredName: z.string(),
  claimNumber: z.string(),
  isRescheduleAllowed: z.boolean(),
  driveStartTime: z.union([z.date(), z.string()]),
  homeArrivalTime: z.union([z.date(), z.string()]),
  peril: z.string(),
  id: z.string(),
  // Blocked-time events expose the externalId we sent on upsert; tasks
  // don't. Optional so existing task payloads still type-check.
  externalId: z.string().optional(),
  resourceId: z.string(),
  taskReferenceId: z.string(),
  name: z.string(),
  eventName: z.string(),
  startDate: z.union([z.string(), z.undefined()]),
  endDate: z.union([z.string(), z.undefined()]),
  source: z.string(),
  notes: z.string(),
  type: z.enum(EVENT_TYPE),
  targetQueue: z.string(),
  isTask: z.boolean(),
  reasonCode: z.string(),
  lossLat: z.number(),
  lossLong: z.number(),
  mustStartAfter: z.date().nullable(),
  mustEndBefore: z.date().nullable(),
  methodOfInspection: z.string().nullable(),
  taskType: z.string().nullable(),
  category: z.string().nullable(),
  isForced: z.boolean().nullable(),
  createdBy: z.string().nullable(),
  editedBy: z.string().nullable(),
});

export const SuperApiSchema = z.object({
  resources: z.array(SuperApiResource),
  calendars: z.array(SuperApiCalendar),
  events: z.array(SuperApiTask),
});

export type SuperApiResponse = z.infer<typeof SuperApiSchema>;
